import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField } from '../../components/ui/TextField';
import { Select } from '../../components/ui/Select';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import { safeReturnPath } from '../../lib/askContext';
import { fetchAiKeyStatus, saveAiKey, type AiKeyStatus } from '../../api/reportApi';
import { CustomPromptsSection } from '../../components/reports/CustomPromptsSection';
import { ApiUsageSection } from '../../components/reports/ApiUsageSection';
import {
  AI_PROVIDERS,
  AI_PROVIDER_NEEDS_KEY,
  AI_PROVIDER_LABEL,
  emptyAiKeys,
  type AIProvider,
} from '../../types';

// AI reports can be generated by any provider in src/config/aiProviders.json.
// Every provider needs a key. A key you type here is saved two
// places: this browser's localStorage (setAiSettings, unchanged - keeps a
// report request's apiKey field working exactly as before, including
// offline) AND, for a provider with a non-empty value, the backend
// (saveAiKey -> POST /ai/keys/{provider}, encrypted at rest - see
// internal/secrets' header comment), so it's also there on a new device/
// browser instead of starting from scratch. The generate action itself
// lives on ReportsScreen.
export function AiSettingsScreen() {
  const { channelId } = useParams<{ channelId: string }>();
  const { aiSettings, setAiSettings, channel, isChannelOwner } = useStore();
  const c = channelId ? channel(channelId) : undefined;
  // Sent here by the Ask dock: offer a way back once a key is in place.
  const from = safeReturnPath(useSearchParams()[0].get('from'));
  const [provider, setProvider] = useState<AIProvider>(aiSettings.provider);
  const [apiKeys, setApiKeys] = useState<Record<AIProvider, string>>({
    ...emptyAiKeys(),
    ...aiSettings.apiKeys,
  });
  // What the backend already has saved, per provider - never the real key
  // (the backend never sends it back), just saved/masked so an empty text
  // field here can still say "a key is already on file" instead of looking
  // like nothing's configured. Refreshed after every save/remove.
  const [keyStatus, setKeyStatus] = useState<Record<string, AiKeyStatus>>({});
  const [removing, setRemoving] = useState<AIProvider | null>(null);

  const loadKeyStatus = () => {
    fetchAiKeyStatus()
      .then((list) => setKeyStatus(Object.fromEntries(list.map((s) => [s.provider, s]))))
      .catch(() => {});
  };

  useEffect(() => {
    loadKeyStatus();
  }, []);

  const auto = useAutoSave({ provider, apiKeys }, async (v) => {
    setAiSettings({
      provider: v.provider,
      apiKeys: AI_PROVIDERS.reduce(
        (acc, p) => ({ ...acc, [p]: (v.apiKeys[p] ?? '').trim() }),
        {} as Record<AIProvider, string>
      ),
    });
    // Only ever push a NON-empty value to the backend here - an empty
    // field must never implicitly clear an already-saved key (e.g. just
    // switching the provider dropdown re-fires this callback with
    // whatever's currently in every field, most of which are usually
    // blank). Clearing a saved key is the explicit "Remove" button below,
    // not a side effect of autosave.
    const toSave = AI_PROVIDERS.filter((p) => AI_PROVIDER_NEEDS_KEY[p] && v.apiKeys[p]?.trim());
    if (toSave.length === 0) return;
    await Promise.all(toSave.map((p) => saveAiKey(p, v.apiKeys[p].trim())));
    loadKeyStatus();
  });
  const saved = auto.status === 'saved';

  const setKey = (p: AIProvider, v: string) => {
    setApiKeys((prev) => ({ ...prev, [p]: v }));
  };

  const removeKey = async (p: AIProvider) => {
    setRemoving(p);
    try {
      await saveAiKey(p, '');
      setKey(p, '');
      loadKeyStatus();
    } catch {
      /* leave the saved key in place on failure - the status refresh above never ran */
    } finally {
      setRemoving(null);
    }
  };

  const hasAnyKey = AI_PROVIDERS.some((p) => (apiKeys[p] ?? '').trim() || keyStatus[p]?.saved);
  const selectedNeedsKey = AI_PROVIDER_NEEDS_KEY[provider] && !apiKeys[provider]?.trim();

  return (
    <div className="flex flex-col gap-5" onBlur={auto.flush}>
      <ScreenHeader title="AI keys" action={<AutoSaveStatus status={auto.status} onRetry={auto.retry} />} />

      {from && (
        <FormSection title="Ask Zmist">
          <p className="text-sm m-0">
            Ask Zmist uses your own AI provider key for explanations and analysis. Channels, charts and reports work without one.
          </p>
          {hasAnyKey ? (
            <Link to={from} className="self-start text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
              Continue where you were
            </Link>
          ) : (
            <p className="text-xs text-black/50 dark:text-white/50 m-0">Add a key below, then continue.</p>
          )}
        </FormSection>
      )}

      <FormSection title="Provider for reports">
        <Select
          label="AI provider"
          value={provider}
          onChange={(e) => setProvider(e.target.value as AIProvider)}
        >
          {AI_PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {AI_PROVIDER_LABEL[p]}
            </option>
          ))}
        </Select>
        <p className="text-xs text-black/50 dark:text-white/50 m-0">
          {selectedNeedsKey
            ? `Add a ${AI_PROVIDER_LABEL[provider]} API key below to use it.`
            : `Reports use your own ${AI_PROVIDER_LABEL[provider]} key — no limit.`}
        </p>
      </FormSection>

      <FormSection title="API keys">
        {AI_PROVIDERS.filter((p) => AI_PROVIDER_NEEDS_KEY[p]).map((p) => (
          <div key={p} className="flex flex-col gap-1">
            <TextField
              label={AI_PROVIDER_LABEL[p] + ' key'}
              type="password"
              placeholder={
                keyStatus[p]?.saved ? `Saved (${keyStatus[p].masked}) - leave blank to keep it` : p === 'anthropic' ? 'sk-ant-...' : 'API key'
              }
              value={apiKeys[p] ?? ''}
              onChange={(e) => setKey(p, e.target.value)}
            />
            {keyStatus[p]?.saved && (
              <button
                type="button"
                onClick={() => removeKey(p)}
                disabled={removing === p}
                className="self-start text-xs text-red-500 hover:underline disabled:opacity-50"
              >
                {removing === p ? 'Removing...' : 'Remove saved key'}
              </button>
            )}
          </div>
        ))}
      </FormSection>

      {c && isChannelOwner(c) && (
        <>
          <CustomPromptsSection nickname={c.nickname} />
          <ApiUsageSection nickname={c.nickname} />
        </>
      )}

      <p className="text-xs text-black/50 dark:text-white/50 text-center m-0">
        {saved
          ? 'Saved. Generate a report from the Reports tab.'
          : 'Keys are saved to your account (encrypted) and to this browser, then sent with each report request.'}
      </p>
    </div>
  );
}
